import { Component, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent } from 'src/app/controls/components/basic/button/button.component';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';
import { CalculateValidationTextFn } from 'src/app/controls/components/internal/control-supporting-text/control-supporting-text.component';

@Component({
    selector: 'ep-partner-onboarding-preset-name-dialog',
    standalone: true,
    templateUrl: './partner-onboarding-preset-name-dialog.component.html',
    styleUrl: './partner-onboarding-preset-name-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ReactiveFormsModule, TextboxComponent, ButtonComponent]
})
export class PresetNameDialogComponent {
    public presetForm: FormGroup;
    public existingName = signal<string>('');
    private existingNames: string[] = [];

    constructor(
        private activeModal: NgbActiveModal,
        private fb: FormBuilder
    ) {
        this.presetForm = this.fb.group({
            presetName: ['', [
                Validators.required, 
                Validators.maxLength(250),
                this.duplicateNameValidator.bind(this)
            ]]
        });
    }

    setExistingName(name: string): void {
        this.existingName.set(name);
        this.presetForm.patchValue({ presetName: name });
    }

    setExistingNames(names: string[]): void {
        this.existingNames = names || [];
        this.presetForm.get('presetName')?.updateValueAndValidity();
    }

    private duplicateNameValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null; 
        }

        const currentName = (control.value as string).trim().toLowerCase();
        const existingName = this.existingName().toLowerCase();
        
        if (currentName === existingName) {
            return null;
        }

        const isDuplicate = this.existingNames.some(name => 
            name.toLowerCase() === currentName
        );

        return isDuplicate ? { duplicateName: { value: control.value as string } } : null;
    }

    getValidationText: CalculateValidationTextFn = control => {
        if (!control?.errors) {
            return null;
        }

        if (control.errors['required']) {
            return 'Preset name is required.';
        }
        
        if (control.errors['maxlength']) {
            return 'Preset name cannot exceed 10 characters.';
        }
        
        if (control.errors['duplicateName']) {
            return 'A preset with this name already exists. Please choose a different name.';
        }

        return null;
    };

    save(): void {
        if (this.presetForm.valid) {
            let presetName = this.presetForm.get('presetName')?.value as string;
            presetName = presetName.trim();
            if (presetName) {
                this.activeModal.close(presetName);
            }
        }
    }

    cancel(): void {
        this.activeModal.dismiss();
    }

    onKeyPress(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.presetForm.valid) {
            this.save();
        } else if (event.key === 'Escape') {
            this.cancel();
        }
    }

    get isFormValid(): boolean {
        let presetName = this.presetForm.get('presetName')?.value as string;
        presetName = presetName.trim();
        return this.presetForm.valid && !!presetName;
    }

    get presetNameControl() {
        return this.presetForm.get('presetName');
    }

    get duplicateNameError(): boolean {
        const control = this.presetNameControl;
        return !!(control?.errors?.['duplicateName'] && (control.dirty || control.touched));
    }
}
